import * as React from "react";
import { Tooltip } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/ui/userAvatar";
import { getUserDisplayName } from "@/helpers/userDisplay";
import { emptyUserData, useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useGetDirectoryUserQuery } from "@/api/users-admin";
import { SnapshotCollectionPermission } from "@/modules/snapshots/snapshotCollection.types";

const MAX_VISIBLE_AVATARS = 3;

interface Props {
  permissions: SnapshotCollectionPermission[];
  onClick: () => void;
}

export const PermissionAvatarStack: React.FC<Props> = ({ permissions, onClick }) => {
  const authenticatedUser = useAuthenticatedUser();

  const otherPermissions = React.useMemo(
    () => permissions.filter(p => p.user_id !== authenticatedUser.id),
    [permissions, authenticatedUser.id],
  );

  if (otherPermissions.length === 0) {
    return null;
  }

  const visiblePermissions = otherPermissions.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = otherPermissions.length - visiblePermissions.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center -space-x-2 rounded-full transition-opacity hover:opacity-80"
      aria-label="Manage collection access"
    >
      {visiblePermissions.map(permission => (
        <SingleUserAvatar key={permission.id} userId={permission.user_id} />
      ))}
      {overflowCount > 0 && (
        <span className="z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-xs font-medium text-gray-700">
          +{overflowCount}
        </span>
      )}
    </button>
  );
};

interface SingleUserAvatarProps {
  userId: string;
}

const SingleUserAvatar: React.FC<SingleUserAvatarProps> = ({ userId }) => {
  const { data: user = emptyUserData } = useGetDirectoryUserQuery(userId);

  return (
    <Tooltip text={getUserDisplayName(user)} delayDuration={100}>
      <UserAvatar user={user} identifier={userId} className="border-2 border-white" />
    </Tooltip>
  );
};
